import { getDatabase, onValue, ref } from "firebase/database";
import { useAtom, useAtomValue } from "jotai";
import orderBy from "lodash/orderBy";
import { useCallback, useEffect } from "react";
import { graphql, useFragment } from "react-relay";
import { Events, scroller } from "react-scroll";

import { getFirebaseApp } from "~/lib/firebase";
import { isAfterOrEqual, isString } from "~/lib/utils";
import type { hooksLiveArticleLiveArticle$key } from "~/queries/__generated__/hooksLiveArticleLiveArticle.graphql";

import type { ItemType } from "./atoms";
import { liveArticleScrollerAtom, liveArticleUpdateAtom } from "./atoms";
import { checkIfElementIsLiveItem, getItemContainerId } from "./helpers";

type ScrollerOption = {
  containerId?: string;
  duration: number;
  offset?: number;
  smooth: boolean;
};

export const useTimelineScrollEffect = () => {
  const [liveArticleScroller, setLiveArticleScroller] = useAtom(liveArticleScrollerAtom);

  const setToScroll = useCallback(
    (entityId: string, toScrollType: ItemType, highlightedEntityId?: string) => {
      setLiveArticleScroller(liveArticleScroller => {
        const payload = {
          ...liveArticleScroller,
          toScrollEntityId: entityId,
          toScrollType,
        };

        if (highlightedEntityId) {
          payload.highlightedEntityId = highlightedEntityId;
        }
        return payload;
      });
    },
    [setLiveArticleScroller],
  );

  const reset = useCallback(() => {
    setLiveArticleScroller(liveArticleScroller => ({
      ...liveArticleScroller,
      highlightedEntityId: "",
      toScrollType: null,
    }));
  }, [setLiveArticleScroller]);

  const setToScrollType = useCallback(
    (toScrollType: ItemType) => {
      setLiveArticleScroller(liveArticleScroller => ({
        ...liveArticleScroller,
        toScrollType,
      }));
    },
    [setLiveArticleScroller],
  );

  const scrollToElement = useCallback(
    (type: "timeline" | "content", option: ScrollerOption) => {
      const toScrollEntityId = liveArticleScroller.toScrollEntityId;
      const toScrollType = liveArticleScroller.toScrollType;
      if (!toScrollEntityId || toScrollType !== type) return;
      scroller.scrollTo(getItemContainerId(type, toScrollEntityId), option);
    },
    [liveArticleScroller],
  );

  useEffect(() => {
    Events.scrollEvent.register("end", to => {
      reset();
      if (to && checkIfElementIsLiveItem(to)) {
        setToScrollType("timeline");
      }
    });
  }, [reset, setToScrollType]);

  return {
    liveArticleScroller,
    reset,
    scrollToElement,
    setToScroll,
  };
};

type ContentStatusSnapshot = {
  latestLiveContent: string;
  updatedDate: number;
};

export const useLiveUpdate = (
  entityUuid: string,
  onUpdateHandler: (data: ContentStatusSnapshot) => void,
) => {
  const [liveArticleUpdate, setLiveArticleUpdate] = useAtom(liveArticleUpdateAtom);

  const newUpdateCount = liveArticleUpdate?.latestLiveContentEntityUuids?.length;

  const appendLatestLiveContentEntityUuid = useCallback(
    (latestLiveContentEntityUuid: string, updatedDate: Date) => {
      if (
        liveArticleUpdate.latestLiveContentEntityUuids.includes(latestLiveContentEntityUuid) ||
        !isAfterOrEqual(updatedDate, liveArticleUpdate.updatedDate)
      )
        return;
      const latestLiveContentEntityUuids = [
        ...liveArticleUpdate.latestLiveContentEntityUuids,
        latestLiveContentEntityUuid,
      ];
      setLiveArticleUpdate(liveArticleUpdate => ({
        ...liveArticleUpdate,
        latestLiveContentEntityUuids,
        updatedDate,
      }));
    },
    [setLiveArticleUpdate, liveArticleUpdate],
  );

  const reset = useCallback(() => {
    setLiveArticleUpdate(liveArticleUpdate => ({
      ...liveArticleUpdate,
      latestLiveContentEntityUuids: [],
      updatedDate: new Date(),
    }));
  }, [setLiveArticleUpdate]);

  useEffect(() => {
    if (!isString(entityUuid) || liveArticleUpdate.isDatabaseSubscribed) return;
    const firebaseApp = getFirebaseApp();
    const database = getDatabase(firebaseApp);

    const contentStatusReference = ref(database, `content_status/${entityUuid}`);
    onValue(contentStatusReference, snapshot => {
      const data = snapshot.val() as ContentStatusSnapshot;
      const { latestLiveContent, updatedDate } = data;
      if (!isAfterOrEqual(updatedDate, liveArticleUpdate.updatedDate)) return;
      onUpdateHandler(data);
      appendLatestLiveContentEntityUuid(latestLiveContent, new Date(updatedDate));
    });

    setLiveArticleUpdate(liveArticleUpdate => ({
      ...liveArticleUpdate,
      isDatabaseSubscribed: true,
    }));
  }, [
    appendLatestLiveContentEntityUuid,
    onUpdateHandler,
    entityUuid,
    liveArticleUpdate,
    setLiveArticleUpdate,
  ]);

  return {
    newUpdateCount,
    reset,
  };
};

export const useFilteredLiveContents = (reference: hooksLiveArticleLiveArticle$key | null) => {
  const liveArticleUpdate = useAtomValue(liveArticleUpdateAtom);

  const content = useFragment<hooksLiveArticleLiveArticle$key>(
    graphql`
      fragment hooksLiveArticleLiveArticle on LiveArticle {
        liveContents {
          entityUuid
          customTime
          entityId
          ...itemLiveArticleContentLiveContent
          ...itemLiveArticleTimelineLiveContent
        }
      }
    `,
    reference,
  );

  const liveContents = orderBy(
    content?.liveContents ?? [],
    liveContent => liveContent?.customTime,
    ["desc"],
  );
  const filteredLiveContents = liveContents.filter(liveContent => {
    const entityUuid = liveContent?.entityUuid;
    if (!isString(entityUuid) || !liveContent?.entityId) return false;
    return !liveArticleUpdate.latestLiveContentEntityUuids.includes(entityUuid);
  });

  return filteredLiveContents;
};
